import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import SubscriptionPage from '@/pages/Subscription';

interface SubscriptionGateState {
  loading: boolean;
  requiresSubscription: boolean;
  hydrated: boolean;
}

async function fetchSubscriptionRequired(): Promise<boolean> {
  const response = await fetch('/api/subscriptions/me', {
    credentials: 'include',
  });
  if (!response.ok) return true;
  const payload = await response.json();
  const subscription = payload?.data?.subscription;
  return subscription?.status !== 'active';
}

export function SubscriptionRequiredCover() {
  const { t } = useTranslation(['subscription', 'common']);
  const { user, loading: authLoading, isApproved, isAdmin } = useAuth();
  const location = useLocation();
  const [gateState, setGateState] = useState<SubscriptionGateState>({
    loading: true,
    requiresSubscription: false,
    hydrated: false,
  });

  useEffect(() => {
    if (authLoading || !user || !isApproved || isAdmin) {
      setGateState({
        loading: false,
        requiresSubscription: false,
        hydrated: true,
      });
      return;
    }

    let cancelled = false;

    const refreshGate = async () => {
      setGateState((current) => ({
        ...current,
        // Keep the current overlay stable during background refreshes.
        loading: !current.hydrated,
      }));
      try {
        const requiresSubscription = await fetchSubscriptionRequired();
        if (!cancelled) {
          setGateState({
            loading: false,
            requiresSubscription,
            hydrated: true,
          });
        }
      } catch {
        if (!cancelled) {
          setGateState((current) => ({
            loading: false,
            requiresSubscription: current.requiresSubscription,
            hydrated: true,
          }));
        }
      }
    };

    void refreshGate();

    const intervalId = window.setInterval(() => {
      void refreshGate();
    }, 10_000);

    const handleFocus = () => {
      void refreshGate();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [authLoading, isAdmin, isApproved, user]);

  if (
    location.pathname === '/subscription' ||
    (gateState.loading && !gateState.hydrated) ||
    !gateState.requiresSubscription
  ) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120] bg-background/95 backdrop-blur-sm">
      <div className="h-full overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto mb-6 flex w-full max-w-3xl items-start gap-4 rounded-3xl border bg-card p-6 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Lock className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t('subscription:plans.choosePlan')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('subscription:description')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('subscription:gate.requiredMessage', {
                  defaultValue: 'Choose a subscription to continue using GIFT.',
                })}
              </p>
            </div>
          </div>

          <div className="mx-auto w-full max-w-6xl rounded-3xl border bg-background/80 p-4 shadow-sm sm:p-6">
            <SubscriptionPage embedded />
          </div>
        </div>
      </div>
    </div>
  );
}
